import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useRegistrationHandler } from '@/components/auth/RegistrationHandler';
import { motion } from 'framer-motion';
import { UserRoles } from '@/contexts/auth/enums';
import TermsOfServiceModal from '@/components/legal/TermsOfServiceModal';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { register } = useRegistrationHandler();
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName) newErrors.firstName = "Kötelező mező";
    if (!lastName) newErrors.lastName = "Kötelező mező";
    if (!email) newErrors.email = "Kötelező mező";
    if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Érvénytelen email formátum";
    if (!password) newErrors.password = "Kötelező mező";
    if (password.length < 6) newErrors.password = "A jelszónak legalább 6 karakter hosszúnak kell lennie";

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await register(
        { firstName, lastName },
        email,
        password,
        undefined,
        UserRoles.USER
      );

      if (result.success) {
        setSubmitted(true);
      } else if (result.message) {
        setFormErrors(prev => ({
          ...prev,
          general: result.message
        }));
      }
    } catch (error) {
      console.error('Registration error:', error);
      setFormErrors(prev => ({
        ...prev,
        general: "Hiba történt a regisztráció során. Kérjük próbálja újra később."
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setTermsModalOpen(true);
  };

  return (
    <section className="py-16 px-4">
      <div className="container mx-auto max-w-3xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-xl border border-medihub-primary/20 p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4">Ne maradjon le a szakmai eseményekről!</h2>
            <p className="text-lg text-gray-600">
              Regisztráljon egyszerűen, hogy személyre szabott értesítéseket kapjon a releváns szakmai eseményekről.             </p>
          </div>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {formErrors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {formErrors.general}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lastName">Vezetéknév</Label>
                  <Input
                    id="lastName"
                    placeholder="Vezetéknév"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={formErrors.lastName ? "border-red-500" : ""}
                  />
                  {formErrors.lastName && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.lastName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="firstName">Keresztnév</Label>
                  <Input
                    id="firstName"
                    placeholder="Keresztnév"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={formErrors.firstName ? "border-red-500" : ""}
                  />
                  {formErrors.firstName && (
                    <p className="text-sm text-red-500 mt-1">{formErrors.firstName}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email cím</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Email címe"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={formErrors.email ? "border-red-500" : ""}
                />
                {formErrors.email && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Jelszó</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Jelszó"
                  value={password}
                  showPasswordToggle={true}
                  onChange={(e) => setPassword(e.target.value)}
                  className={formErrors.password ? "border-red-500" : ""}
                />
                {formErrors.password && (
                  <p className="text-sm text-red-500 mt-1">{formErrors.password}</p>
                )}
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                Regisztrációjával elfogadja az <a href="#" onClick={handleTermsClick} className="underline">adatkezelési tájékoztatónkat</a>. Bármikor leiratkozhat az értesítésekről.
              </p>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Regisztráció folyamatban..." : "Értesítéseket kérek"}
              </Button>
            </form>
          ) : (
            <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-center gap-2 mb-3 text-green-600">
                <Check className="h-5 w-5" />
                <span className="font-medium">Sikeres regisztráció!</span>
              </div>
              <p className="text-gray-600 mb-4">
                Köszönjük! Küldtünk egy e-mailt a regisztráció befejezéséhez szükséges linkkel.
              </p>
              <Button variant="outline" onClick={() => navigate('/login')} className="mt-2">
                Tovább a bejelentkezésre
              </Button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-center">
              <Button variant="outline" asChild>
                <a href="/calendar" className="flex items-center gap-2">
                  Böngésszen az eseményeink között
                  <ChevronRight className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      <TermsOfServiceModal 
        open={termsModalOpen} 
        onOpenChange={setTermsModalOpen} 
      />
    </section>
  );
};

export default RegistrationForm;
